/* eslint-disable no-nested-ternary */
import { useState } from 'react'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import { chakra, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table'

export type DataTableProps<Data extends object> = {
    data: Data[]
    columns: ColumnDef<Data, any>[]
    onRowClick?: (data: Data) => void
    selectedRow?: string | number | null
    defaultSorting?: SortingState
    enableRowHover?: boolean
}

export function DataTable<Data extends object>({
    data,
    columns,
    onRowClick,
    selectedRow,
    defaultSorting = [],
    enableRowHover = false,
}: DataTableProps<Data>) {
    const [sorting, setSorting] = useState<SortingState>(defaultSorting)
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    })

    return (
        <Table width="full" sx={{ tableLayout: 'auto' }} variant="simple">
            <Thead position="sticky" top={0} zIndex={10} bg="white">
                {table.getHeaderGroups().map(headerGroup => (
                    <Tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <Th
                                key={header.id}
                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                onClick={header.column.getToggleSortingHandler()}>
                                {flexRender(header.column.columnDef.header, header.getContext())}

                                <chakra.span pl="4">
                                    {header.column.getIsSorted() ? (
                                        header.column.getIsSorted() === 'desc' ? (
                                            <TriangleDownIcon aria-label="sorted descending" />
                                        ) : (
                                            <TriangleUpIcon aria-label="sorted ascending" />
                                        )
                                    ) : null}
                                </chakra.span>
                            </Th>
                        ))}
                    </Tr>
                ))}
            </Thead>
            <Tbody>
                {table.getRowModel().rows.map(row => {
                    const rowId = (row.original as Data & { id: string | number }).id
                    const isHovered = enableRowHover && row.id === hoveredRowId
                    const isSelected = rowId !== undefined && rowId === selectedRow

                    return (
                        <Tr
                            key={row.id}
                            cursor={onRowClick ? 'pointer' : 'default'}
                            onClick={() => onRowClick?.(row.original)}
                            onMouseEnter={enableRowHover ? () => setHoveredRowId(row.id) : undefined}
                            onMouseLeave={enableRowHover ? () => setHoveredRowId(null) : undefined}
                            bg={isSelected ? 'teal.100' : undefined}
                            _hover={{ bg: isSelected ? 'teal.200' : 'gray.50' }}>
                            {row.getVisibleCells().map(cell => {
                                const showOnHover = (cell.column.columnDef.meta as any)?.showOnHover

                                return (
                                    <Td key={cell.id}>
                                        <chakra.div
                                            visibility={
                                                (showOnHover && (isHovered || isSelected)) || !showOnHover
                                                    ? 'visible'
                                                    : 'hidden'
                                            }>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </chakra.div>
                                    </Td>
                                )
                            })}
                        </Tr>
                    )
                })}
            </Tbody>
        </Table>
    )
}
